/**
 * Chat tab — PLT-4915.
 *
 * A quiet launchpad for the chat side panel: composer card (web-search
 * toggle + textarea + yellow send button) + a list of suggested starter
 * prompts. Pressing Enter / clicking the send arrow hands off to the
 * existing `ChatSidePanel` via `openChatPanel(...)` and closes this
 * drill-in panel.
 *
 * Why hand off instead of embedding a chat thread here:
 *   1. The two panels live in the same right-edge slot — having both
 *      open at once would stack and look broken.
 *   2. `ChatSidePanel` already has the full assistant-ui runtime,
 *      tool-routing, citations, MCP-app iframes, etc.
 *   3. The drill-in is intentionally compact — the chat panel is the
 *      surface where the user does the actual research.
 *
 * Design notes (Janne review v3, May 2026):
 *   - The composer is a pixel-for-pixel re-creation of the live chat
 *     composer's *landing* silhouette: 32px rounded card, warm-tinted
 *     shadow, 44px yellow circle send button, ghost pills (gray-500 →
 *     gray-900 + gray-100 fill on hover, no border), lilac active state
 *     for the web-search pill with a small checkmark badge. The numbers
 *     are kept in sync with `upright-chat-theme.css`'s `.aui-composer-
 *     root--landing` block — when the live composer's design moves,
 *     these styled-components should follow it. (We can't reuse the
 *     `.aui-composer-root` CSS directly because that stylesheet only
 *     loads inside the chat surface — the drill-in panel is mounted at
 *     the app root and doesn't import the theme file.)
 *   - Web search toggle: `useLauncherWebSearchToggle`. On the default
 *     provider it writes the same web search store the live `UprightComposer`
 *     reads, so the choice carries into the chat panel. In EU chats the pill
 *     starts off here and its choice rides along in the panel seed, which the
 *     panel applies after the new chat's reset (PLT-7271).
 *   - Attach files: dropped from this tab entirely. The drill-in doesn't
 *     have its own assistant-ui runtime, so a real file picker can't
 *     open here, and an "Add files" pill that just hands off to the
 *     chat panel (without auto-opening the picker there) is misleading
 *     — the user clicks "Add files" and gets a chat panel, not a file
 *     picker. The chat panel's own "Add files" pill is one click away
 *     after handoff, which is fine. (Janne review v10.)
 *   - Starter prompts are a vertical list of full-width pills (one row
 *     per prompt) — the soft tier of the brand yellow, matching the
 *     `.aui-thread-welcome-suggestion` tier. The 2×2 grid the previous
 *     cut shipped with broke when the "compare to peers" prompt
 *     overflowed at 520px panel width; the single-column list scales
 *     gracefully.
 */
import { ArrowRight, Earth } from '@upright/ui/icons';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { useLauncherWebSearchToggle } from '@upright/ui/chat/use-chat-web-search';
import { openChatPanel } from '@upright/ui/chat/chat-panel-state';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip';
import { LeadFormId } from '@upright/ui/common/activities/lead-form/types';
import { useGetLoggedInUser, useIsSelfSignupCompanyUser, useIsTeam } from '@upright/ui/common/generic/hooks';
import { useCompanyDrilldownContributions } from '@upright/ui/views/model-entity-views/company-view/drilldown/use-get-drilldown-contributions';
import { keyBy, mapValues } from 'lodash';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { anySideBreakdownAvailable, buildChips, ChipContext, extractTopContributors } from './chat-chips';
import {
  closeNetImpactDrillIn,
  drillInImpactCodes,
  drillInSeedKey,
  drillInSingleImpactCode,
  NetImpactDrillInPayload,
} from '../state';

interface Props {
  payload: NetImpactDrillInPayload;
  /** Friendly display name of the impact category, e.g. "Physical diseases". */
  impactLabel: string;
  /** Friendly display name of the entity, e.g. "Siemens". */
  entityName: string;
  /** The valence side the user chose in the panel, which the "why" suggestion follows. */
  selectedValence: ChipContext['selectedValence'];
}

/**
 * Starter prompts (Janne review v8). The static `CHIPS` array that
 * lived here through v7 has moved to `chat-chips.ts` as a data-aware,
 * valence-adaptive `buildChips()` generator — see the docstring there
 * for the slot layout and the seed strategy. The chat tab still
 * renders the same vertical pill list, only the source of the chip
 * definitions changed.
 */

/**
 * What the Chat tab should show for the current viewer (PLT-5427).
 *
 *   - `composer` — full chat launchpad (team users + any org that holds
 *     the `USE_AI_CHAT_WEB` privilege, i.e. nearly all customers/trials).
 *   - `upsell`   — "book a demo" lead-gen card, for viewers we treat as
 *     inbound leads: logged-out visitors and members of the public
 *     self-signup org (`COMPANY_PLATFORM_SELF_SIGNUP_USERS`) without chat.
 *   - `hidden`   — drop the tab entirely. Logged-in users in a real
 *     provisioned org/user-group that lacks chat: they're pipeline /
 *     existing customers, so a "book a demo" pitch for something most of
 *     them already have is the wrong message (Roni's call in Slack).
 */
export type NetImpactChatTabMode = 'composer' | 'upsell' | 'hidden';

/**
 * Resolve the Chat-tab mode for the current viewer. Read once in the
 * panel so the tab-strip (whether to render the Chat tab at all) and the
 * tab body (composer vs. upsell) stay in lockstep — see `panel.tsx`.
 *
 * `USE_AI_CHAT_WEB` is the same resourceless privilege that gates `/chat`
 * and every "Ask AI" entry point, so this surface now flips on the same
 * lever as the rest of the app. While the privilege query is in flight we
 * fall back to `composer` rather than briefly hiding the tab and then
 * popping it in: a flash of the composer for a no-chat org is less jarring
 * than a tab that appears late, and the composer's own submit path is
 * still privilege-checked downstream.
 */
export function useNetImpactChatTabMode(): NetImpactChatTabMode {
  const isTeam = useIsTeam();
  const loggedOut = useGetLoggedInUser() === undefined;
  const isSelfSignup = useIsSelfSignupCompanyUser();
  const { hasPrivilege, loading } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_AI_CHAT_WEB);
  if (isTeam || hasPrivilege || loading) {
    return 'composer';
  }
  // No chat privilege from here on. Inbound leads (logged-out or
  // self-signup org) get the upsell; provisioned orgs get the tab hidden.
  if (loggedOut || isSelfSignup) {
    return 'upsell';
  }
  return 'hidden';
}

export function ChatTab({ payload, impactLabel, entityName, selectedValence }: Props) {
  // The panel drops the Chat tab from the strip when the mode is
  // `hidden`, so this component only ever renders for `composer` /
  // `upsell`. The defensive `hidden` branch keeps the two surfaces in
  // sync if a future caller mounts the tab without the strip guard.
  const mode = useNetImpactChatTabMode();
  if (mode === 'upsell') {
    return <DemoUpsell impactLabel={impactLabel} entityName={entityName} />;
  }
  if (mode === 'hidden') {
    return null;
  }
  return (
    <ChatComposerTab
      payload={payload}
      impactLabel={impactLabel}
      entityName={entityName}
      selectedValence={selectedValence}
    />
  );
}

function ChatComposerTab({ payload, impactLabel, entityName, selectedValence }: Props) {
  const [draft, setDraft] = useState('');
  const webSearch = useLauncherWebSearchToggle();

  // Pull the contributors data so slot 2 of the chip generator can
  // name the largest positive/negative product directly (Janne review
  // v8). The hook is the same one the Positive/Negative tabs use — so
  // when the user opens the panel and lands on a contributor tab
  // first, the chat tab gets the data for free from the react-query
  // cache; opening directly into chat just fires the same fetch one
  // tab earlier. We don't block on the result: until it lands, the
  // chip generator falls back to the generic "Why is it net-X?"
  // wording.
  const drilldownQuery = useCompanyDrilldownContributions(payload.modelResultId);

  const chips = useMemo(() => {
    const productNamesById = mapValues(
      keyBy(payload.productNames ?? [], d => d.id),
      d => d.name,
    );
    const impactCodes = drillInImpactCodes(payload.target);
    const { topPositive, topNegative } = extractTopContributors(
      drilldownQuery.data,
      impactCodes,
      productNamesById,
      payload.categorySideCentsPerDollar,
    );
    const ctx: ChipContext = {
      entityName,
      impactLabel,
      entityId: payload.entity.id,
      seedKey: drillInSeedKey(payload.target),
      topPositive,
      topNegative,
      selectedValence,
      positiveCentsPerDollar: payload.positiveCentsPerDollar,
      negativeCentsPerDollar: payload.negativeCentsPerDollar,
      anySideBreakdownAvailable: anySideBreakdownAvailable(impactCodes, payload.categorySideCentsPerDollar),
      impactCode: drillInSingleImpactCode(payload.target),
    };
    return buildChips(ctx);
    // `drilldownQuery.data` identity changes when react-query swaps the
    // payload, and `selectedValence` when the user switches valence tab;
    // the rest are stable per panel open.
  }, [
    drilldownQuery.data,
    payload.productNames,
    payload.target,
    payload.categorySideCentsPerDollar,
    payload.positiveCentsPerDollar,
    payload.negativeCentsPerDollar,
    payload.entity.id,
    entityName,
    impactLabel,
    selectedValence,
  ]);

  function handleSubmit(promptOverride?: string, extra?: Record<string, string>) {
    const prompt = (promptOverride ?? draft).trim();
    if (!prompt) return;
    // Close the drill-in BEFORE opening the chat panel: both panels
    // render in the same right-edge slot, so stacking them produces a
    // visible double-card. `openChatPanel` is a pure state mutation
    // and safe to call in the same tick as `closeNetImpactDrillIn`.
    closeNetImpactDrillIn();
    openChatPanel({
      prompt,
      entityId: payload.entity.id,
      entityType: payload.entity.type,
      // Re-use the existing `dimension_bar` source tag — same
      // analytics bucket as the previous direct chat-panel handoff
      // from impact-category clicks (see `impact-bars.tsx`).
      source: 'dimension_bar',
      pageContext: payload.pageContext,
      ...(extra ? { extra } : {}),
      ...(webSearch.handOff ? { webSearch: webSearch.handOff } : {}),
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter inserts a newline. Same convention as
    // the live chat composer (`upright-chat-composer.tsx`).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  // Janne review v5: drop the "Ask Upright's AI about <Siemens>'s …"
  // intro entirely. The intro previously bolded the entity name, which
  // pulled attention to the wrong word ("Siemens" is already named
  // three times above in the title, definition, stat row and tab
  // strip); removing the line takes the redundancy out and the bolding
  // problem with it. The company + category phrase moves into the
  // composer placeholder so first-time readers still see the context
  // they need before typing.
  const composerPlaceholder = `Ask about ${entityName}'s ${impactLabel.toLowerCase()}…`;

  const webSearchPill = (
    <ActionPill
      type="button"
      $variant="web-search"
      $active={webSearch.enabled}
      aria-pressed={webSearch.enabled}
      aria-label="Web search"
      onClick={webSearch.toggle}
    >
      <PillIcon>
        <Earth width={18} height={18} />
      </PillIcon>
      <PillLabel>Web search</PillLabel>
    </ActionPill>
  );

  return (
    <Root>
      <ComposerCard>
        <ComposerTextarea
          aria-label="Ask Upright"
          placeholder={composerPlaceholder}
          value={draft}
          onChange={e => {
            setDraft(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <ComposerActions>
          <PillCluster>
            {/* In EU chats the tooltip says where search terms go (PLT-7271). */}
            {webSearch.available &&
              (webSearch.offAtChatStart ? (
                <Tooltip text={webSearch.tooltip} isNonFocusableTarget>
                  {webSearchPill}
                </Tooltip>
              ) : (
                webSearchPill
              ))}
          </PillCluster>
          <SendButton
            type="button"
            aria-label="Send"
            disabled={draft.trim().length === 0}
            onClick={() => {
              handleSubmit();
            }}
          >
            <SendArrow aria-hidden="true" />
          </SendButton>
        </ComposerActions>
      </ComposerCard>

      <StarterEyebrow>Or try one of these</StarterEyebrow>
      <StarterList>
        {chips.map((chip, i) => (
          <StarterItem
            key={i}
            type="button"
            onClick={() => {
              handleSubmit(chip.prompt, chip.extra);
            }}
          >
            <StarterText>{chip.text}</StarterText>
            <StarterArrow aria-hidden="true">
              <ArrowRight width={14} height={14} />
            </StarterArrow>
          </StarterItem>
        ))}
      </StarterList>
    </Root>
  );
}

/**
 * "Book a demo" upsell (PLT-4940, PLT-5427). A deliberately simple,
 * current-style card — much lighter than the legacy lock-icon "Request
 * demo" overlay — that mentions chat and routes to the existing
 * `book-demo-csrd` lead form (the same form the DMA / financial-effects
 * paywalls open). The `?lfid=` search-param navigation is what opens the
 * lead-form modal on the company profile route; we close the drill-in
 * first so the form isn't stacked behind it.
 *
 * Shown to viewers we treat as inbound leads: logged-out visitors and
 * logged-in members of the public self-signup org without chat. Real
 * provisioned orgs without chat never see this — their Chat tab is
 * dropped from the strip instead (see `useNetImpactChatTabMode`).
 */
function DemoUpsell({ impactLabel, entityName }: { impactLabel: string; entityName: string }) {
  return (
    <UpsellRoot>
      <UpsellTitle>Chat with Upright&apos;s AI</UpsellTitle>
      <UpsellBody>
        Ask follow-up questions about {entityName}&apos;s {impactLabel.toLowerCase()} impact — what drives it, how
        it&apos;s measured, and how it compares to peers — with Upright&apos;s AI assistant. Chat is available to
        Upright users.
      </UpsellBody>
      <DemoButton
        to={`?lfid=${LeadFormId.BOOK_DEMO_CSRD}`}
        onClick={() => {
          closeNetImpactDrillIn();
        }}
      >
        Book a demo <DemoArrow aria-hidden="true">→</DemoArrow>
      </DemoButton>
    </UpsellRoot>
  );
}

/* ─── styled ─────────────────────────────────────────────────────────── */

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
`;

/* Janne review v5 removed the "Ask Upright's AI about <Siemens>'s …"
   intro entirely; the company + category phrase now lives in the
   composer placeholder. The previous `Intro` styled component lives on
   in git history only — no consumers left. */

/* Eyebrow sitting between the composer card and the starter pill list.
   Tiny gray-500 uppercase tracking — same visual weight as the stat
   row labels in the panel header, so the surface reads as composer
   (primary) → "or try one of these" (secondary) → pill list.

   Janne review v6: the v5 cut had the eyebrow visually glued to the
   chip list (negative bottom margin pulled the eyebrow onto the
   list), which made the chips read as a continuation of the eyebrow
   line rather than a labelled list below it. v6 drops the negative
   margin entirely and adds a positive `--space__sm` gap so the
   eyebrow sits as a header above the chips with breathing room
   between the two. The Root's `--space__lg` gap still applies above
   the eyebrow (between the composer card and the eyebrow). */
const StarterEyebrow = styled.div`
  margin-top: var(--space__xs);
  margin-bottom: var(--space__sm);
  font-size: var(--font-size__xs);
  font-weight: var(--font-weight__medium);
  letter-spacing: 0.04em;
  color: var(--color__gray-600);
  text-transform: uppercase;
`;

/* Composer card — pixel-matched to `.aui-composer-root--landing` in
   `upright-chat-theme.css`. 32px radius so the bottom-right corner curls
   cleanly around the 44px circular send button (button radius 22 +
   composer padding 12 ≈ 34, rounded to 32). Warm-tinted shadow sits
   better on the warm off-white panel BG than a flat blue-grey one.
   Focus state adds a 4px lilac glow ring stacked on top of the warm
   layer — same composition the live composer uses. */
const ComposerCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
  background: #ffffff;
  border: 1px solid var(--color__gray-200);
  border-radius: 32px;
  padding: var(--space__md);
  box-shadow:
    0 1px 3px hsl(36 25% 50% / 0.08),
    0 6px 24px hsl(36 25% 40% / 0.06);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  &:focus-within {
    border-color: hsl(243 56% 55% / 0.45);
    box-shadow:
      0 1px 3px hsl(36 25% 50% / 0.08),
      0 6px 24px hsl(36 25% 40% / 0.06),
      0 0 0 4px hsl(243 56% 55% / 0.08);
  }
`;

const ComposerTextarea = styled.textarea`
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  font: inherit;
  font-size: var(--font-size__md);
  color: var(--color__gray-900);
  padding: 0 var(--space__sm);
  line-height: 1.5;
  min-height: 3rem;
  max-height: 8rem;
  &::placeholder {
    color: var(--color__gray-500);
    opacity: 1;
    font-weight: var(--font-weight__regular);
  }
`;

const ComposerActions = styled.div`
  display: flex;
  align-items: end;
  gap: var(--space__sm);
`;

const PillCluster = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
`;

/* Ghost pill — transparent rest, gray-500 text/icon, hover bumps to
   gray-900 + gray-100 fill. Mirrors `.upright-action-pill` in
   `upright-chat-theme.css`. The web-search variant gets a lilac fill +
   purple text when active (with a checkmark badge pinned to the icon's
   bottom-right). */
const ActionPill = styled.button<{ $active?: boolean; $variant?: 'web-search' | undefined }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space__sm);
  height: 36px;
  padding: 0 var(--space__md);
  border: none;
  border-radius: var(--border-radius__full);
  background: transparent;
  color: var(--color__gray-500);
  font-family: inherit;
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  line-height: 1;
  cursor: pointer;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
  &:hover {
    color: var(--color__gray-900);
    background-color: var(--color__gray-100);
  }
  &:focus-visible {
    outline: var(--focus-ring__width) solid var(--focus-ring__color);
    outline-offset: 2px;
  }
  ${p =>
    p.$variant === 'web-search' &&
    p.$active &&
    css`
      color: var(--color__lilac-600);
      background-color: var(--color__lilac-100);
      &:hover {
        color: var(--color__lilac-700);
        background-color: var(--color__lilac-200);
      }
    `}
`;

const PillIcon = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  /* Active checkmark badge — small purple circle pinned to the bottom-
     right of the globe icon when web search is on. The parent button
     toggles via aria-pressed; we key the badge off [aria-pressed="true"]
     so we don't need an extra modifier class on this inner span. */
  ${ActionPill}[aria-pressed='true'] & {
    &::after {
      content: '';
      position: absolute;
      right: -4px;
      bottom: -4px;
      width: 10px;
      height: 10px;
      border-radius: var(--border-radius__full);
      background-color: var(--color__lilac-600);
      border: 1.5px solid #ffffff;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M2.5 5.2 4.2 6.8 7.5 3.4'/%3E%3C/svg%3E");
      background-size: 8px 8px;
      background-repeat: no-repeat;
      background-position: center;
      pointer-events: none;
    }
  }
`;

const PillLabel = styled.span`
  white-space: nowrap;
`;

/* Send button — 44px brand-yellow circle, mirrors `.aui-composer-send` in
   `upright-chat-theme.css`. The loud tier resting → its hover token, with a
   small +6% scale on hover to echo the send-button bounce in the live
   composer. DESIGN.md restricts the brand yellow to AI entry points; this
   tab IS the chat-launchpad surface and hands directly to the chat panel. */
const SendButton = styled.button`
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color__brand-yellow-loud);
  color: var(--color__gray-900);
  border: none;
  border-radius: var(--border-radius__full);
  cursor: pointer;
  flex-shrink: 0;
  align-self: center;
  transition:
    background 0.15s ease,
    transform 0.1s ease,
    opacity 0.15s ease;
  &:hover:not(:disabled) {
    background: var(--color__brand-yellow-loud-hover);
    transform: scale(1.06);
  }
  &:active:not(:disabled) {
    transform: scale(0.96);
  }
  &:disabled {
    background: var(--color__gray-200);
    color: var(--color__gray-400);
    cursor: default;
    transform: none;
  }
`;

/* Up-arrow mask — same geometry as the live composer's send button
   (`.aui-composer-send::after` in `upright-chat-theme.css`, stroke-width
   3, round caps). Inlining the SVG mask here so this button is self-
   contained and doesn't depend on the chat theme stylesheet being
   loaded — the drill-in panel mounts at the app root and the theme CSS
   only ships with the chat surface. */
const SendArrow = styled.span`
  display: block;
  width: var(--space__lg);
  height: var(--space__lg);
  background: currentColor;
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 19V5'/%3E%3Cpath d='m5 12 7-7 7 7'/%3E%3C/svg%3E");
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 19V5'/%3E%3Cpath d='m5 12 7-7 7 7'/%3E%3C/svg%3E");
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
`;

/* Starter chips — vertical list of pill-shaped buttons. The soft tier
   resting → its hover token (which aliases the loud tier), matching the
   live chat's welcome-state suggestion pills
   (`.aui-thread-welcome-suggestion` in `upright-chat-theme.css`). Reusing
   the same two tokens ties this surface visually to the chat welcome state
   it hands off to. */
const StarterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

const StarterItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space__md);
  width: 100%;
  background: var(--color__brand-yellow-soft);
  border: none;
  border-radius: var(--border-radius__full);
  padding: var(--space__sm) var(--space__lg);
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-900);
  cursor: pointer;
  text-align: left;
  line-height: 1.3;
  letter-spacing: -0.005em;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
  &:hover {
    background: var(--color__brand-yellow-soft-hover);
    box-shadow: 0 2px 10px hsl(66 100% 60% / 0.35);
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: var(--focus-ring__width) solid var(--focus-ring__color);
    outline-offset: 2px;
  }
`;

const StarterText = styled.span`
  flex: 1;
  min-width: 0;
  /* Janne review v4 made the chip text specific (it now spells out
     the entity + category, e.g. "How does Siemens compare to peers on
     knowledge?"). Some of these don't fit on a single line at 520px
     panel width, so let them wrap rather than ellipsize. \`text-wrap:
     pretty\` keeps the second line from ending on a lone word. */
  white-space: normal;
  text-wrap: pretty;
`;

/* Right-edge arrow on each starter chip. Resting opacity is 0.5 so the
   arrow whispers; hover (driven by the parent button's hover) bumps to
   full opacity + a 2px nudge to the right, which signals "this is the
   action affordance" without competing with the chip's yellow fill.
   Janne review v5 polish. */
const StarterArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color__gray-900);
  flex-shrink: 0;
  opacity: 0.5;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  ${StarterItem}:hover & {
    opacity: 1;
    transform: translateX(2px);
  }
`;

/* ─── Logged-out demo upsell (PLT-4940) ──────────────────────────────── */

const UpsellRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
  align-items: flex-start;
  padding: var(--space__xl);
  background: var(--color__gray-50);
  border: 1px solid var(--color__gray-200);
  border-radius: var(--border-radius__lg);
`;

const UpsellTitle = styled.div`
  font-size: var(--font-size__lg);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  line-height: 1.3;
`;

const UpsellBody = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-700);
  line-height: 1.5;
  text-wrap: pretty;
`;

/* Compact pill demo button — gray-900 fill, white label, small arrow
   that nudges on hover. Intentionally simpler than the legacy
   `RequestDemoButton` (1.25rem black slab) per Janne's "much simpler,
   current style" note. */
const DemoButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space__sm);
  margin-top: var(--space__xs);
  padding: var(--space__sm) var(--space__lg);
  background: var(--color__gray-900);
  color: #ffffff;
  text-decoration: none;
  border-radius: var(--border-radius__full);
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  transition:
    background 0.15s ease,
    transform 0.1s ease;
  &:hover {
    background: var(--color__gray-700);
  }
`;

const DemoArrow = styled.span`
  display: inline-block;
  transition: transform 0.15s ease;
  ${DemoButton}:hover & {
    transform: translateX(3px);
  }
`;
